import { useAMCQueryData } from "../hooks/useAMCQueryData";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  BASE_PATH_TO_PURCHASE_REPORT_CONFIG,
  CANNED_QUERIES,
  DEFAULT_LOOKBACK_PERIOD_IN_DAYS,
  FEATURED_REPORTS,
  normalizeQueryName,
} from "../utils/utils";
import { buildTextColumn } from "../../merchandising/components/columns/buildTextColumn";
import { toTitleCase } from "../../../utils/formatText";
import ExpanderContainer from "../../../components/core/basic/ExpanderContainer";
import { useAppSelector } from "../../../redux/hooks";
import useCustomTableFilters from "../../../hooks/useCustomTableFilters";
import Table from "../../merchandising/components/tables/BulkActionsTable";
import { Link } from "@tanstack/react-router";
import { linkOptionsFromHref } from "../../../utils/searchParams";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { FaCog, FaTrash } from "react-icons/fa";
import { Button, buttonRecipe } from "../../../components/core/basic/Button";
import { Flex } from "../../../../styled-system/jsx";
import ConfirmationModal from "../../../components/core/blocks/ConfirmationModal";
import { css } from "../../../../styled-system/css";
import { Switch } from "../../../components/fields/base/Switch";
import AMCReportsListing from "../../../views/amc/AMCReportsListing";
import { useAmcLifecycleFilters } from "./audience/lifecycleFilters";
import useTableSettings from "../../../hooks/useTableSettings";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import TagsReviewDisplay from "../../../components/core/blocks/TagsReviewDisplay";
import { useSimpleCan } from "../../../config/casl/casl";

const AMCSubscriptionsTable = ({
  selectedQueryToExpand,
  setSelectedQuery,
  setShowQueryForm,
  toggleStatusHandler,
  unsubscribeHandler,
  setSelectedQueryToExpand,
}) => {
  const { t } = useTranslation(["amc"]);
  const { AMC_REPORT_TYPE_FILTER } = useAmcLifecycleFilters();
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const TABLE_ID = `amcSubscriptionDetailsTable`;
  const tableRef = useRef();
  const userCanUpdateSubscriptions = useSimpleCan("update", "AMC");
  const userCanDeleteSubscriptions = useSimpleCan("delete", "AMC");

  const additionalFilters = [AMC_REPORT_TYPE_FILTER];
  const initialFilters = selectedQueryToExpand ? [{ ...AMC_REPORT_TYPE_FILTER, value: [selectedQueryToExpand] }] : null;

  const filterProps = useCustomTableFilters(marketPlace, TABLE_ID, {});
  const { updateTableSettings: setSavedTableFilters } = useTableSettings(TABLE_ID, "tableFilters", []);

  useEffect(() => {
    if (selectedQueryToExpand) {
      setSavedTableFilters(initialFilters);
      setSelectedQueryToExpand(null);
    }

    // oxlint-disable-next-line exhaustive-deps
  }, [selectedQueryToExpand]);

  const { data: subscribedQueriesData, isLoading } = useAMCQueryData();

  const tableData = useMemo(() => {
    const basePathToPurchaseReportMeta = FEATURED_REPORTS.find(
      (report) => report.name === "PATH_TO_PURCHASE_GROUPS_INTERNAL",
    );
    const basePathToPurchaseReportSpec = {
      ...basePathToPurchaseReportMeta,
      ...BASE_PATH_TO_PURCHASE_REPORT_CONFIG,
    };

    const data = [...subscribedQueriesData, basePathToPurchaseReportSpec]?.map((row) => {
      const normalizedQueryType = normalizeQueryName(row?.query_type);
      const subscriptionQuerySpec =
        [...CANNED_QUERIES, ...FEATURED_REPORTS].find((querySpec) => {
          return querySpec.name === normalizedQueryType;
        }) ?? {};
      // Set default lookback period for legacy subscriptions before parameters could be customized
      if (subscriptionQuerySpec?.parameter_list?.includes("days") && "days" in row.parameters === false) {
        row.parameters = { ...row.parameters, days: DEFAULT_LOOKBACK_PERIOD_IN_DAYS };
      }
      const subscription_title = row.parameters?.subscription_title?.trim?.() || "";
      const displayNameFromRow = row?.display_name?.trim?.();
      const specDisplayName =
        typeof subscriptionQuerySpec?.display_name === "function" ?
          t(subscriptionQuerySpec.display_name)
        : subscriptionQuerySpec?.display_name;
      const fallbackDisplayName =
        displayNameFromRow ||
        specDisplayName ||
        (normalizedQueryType ? toTitleCase(normalizedQueryType.replaceAll("_", " ").toLowerCase()) : row?.query_type);
      return { ...subscriptionQuerySpec, ...row, display_name: fallbackDisplayName, subscription_title };
    });
    return data?.sort((a, b) => {
      return a.id - b.id;
    });
  }, [subscribedQueriesData, t]);

  const tableColumns = useMemo(() => {
    return [
      buildTextColumn(
        "subscription_title",
        t(($) => $.amc["Title"]),
        {
          formatter: (v) => <span>{v || "—"}</span>,
          hideFilter: true,
          width: 150,
        },
      ),
      {
        key: "link",
        compareKey: null,
        options: {
          className: "text-start justify-content-start px-5 h-100 py-4",
          cellContainerClasses: "text-start justify-content-start",
          metric: {
            id: "link",
            format: "text",
            accessor: (d) => d,
            name: t(($) => $.amc["Link"]),
          },
          formatter: (row) => {
            const { id, isStatic, href } = row;
            return (
              <Link
                style={{ color: trellisPalette[13], fontWeight: 600 }}
                className="fs-standard"
                {...(isStatic ?
                  linkOptionsFromHref(href)
                : { to: "/user/amc/dashboard/reports/$id", params: { id: String(id) } })}
              >
                {t(($) => $.amc["View Reports"])}
              </Link>
            );
          },
          hideFilter: true,
        },
      },
      buildTextColumn(
        "frequency",
        t(($) => $.amc["Frequency"]),
        {
          formatter: (v) => {
            const label =
              v === "daily" ? t(($) => $.amc["Daily"])
              : v === "weekly" ? t(($) => $.amc["Weekly"])
              : v === "monthly" ? t(($) => $.amc["Monthly"])
              : toTitleCase(v);
            return <span>{label}</span>;
          },
          hideFilter: true,
        },
      ),
      buildTextColumn(
        "parameters",
        t(($) => $.amc["Lookback Period"]),
        {
          formatter: (v) => {
            return <span>{t(($) => $.amc["{count} Days"], { count: v.days })}</span>;
          },
          hideFilter: true,
        },
      ),
      buildTextColumn(
        "parameters",
        t(($) => $.amc["Tags"]),
        {
          width: 170,
          className: "text-start justify-content-start px-5 h-100",
          cellContainerClasses: "text-start justify-content-start",
          formatter: (parameters) => {
            return (
              <div className="text-start text-wrap">
                {Object.keys(parameters)?.map((param) => {
                  switch (param) {
                    case "tag":
                      return (
                        <Flex key={param} className={css({ maxHeight: "0" })}>
                          <TagsReviewDisplay tags={parameters[param]} />
                        </Flex>
                      );
                    default:
                      return null;
                  }
                })}
              </div>
            );
          },
          hideFilter: true,
        },
      ),
      {
        key: "actions",
        compareKey: null,
        options: {
          className: "text-start justify-content-start px-5 h-100 pt-5",
          cellContainerClasses: "text-start justify-content-start",
          metric: {
            id: "actions",
            format: "text",
            accessor: (d) => d,
            name: t(($) => $.amc["Actions"]),
            width: 30,
          },

          formatter: (subscription) => {
            const { id, display_name, isStatic } = subscription;
            if (isStatic) {
              return (
                <Flex alignItems="center" gap="large" justifyContent={"center"}>
                  <HoverInfo iconCss={{ color: "gray" }}>
                    {t(($) => $.amc["Actions are unavailable for the base version of featured reports."])}
                  </HoverInfo>
                </Flex>
              );
            }
            return (
              <Flex alignItems="center" gap="medium" justifyContent={"flex-end"}>
                <Button
                  variant={"contentOnly"}
                  onPress={() => {
                    setSelectedQuery({ ...subscription, subscribed: true });
                    setShowQueryForm(true);
                  }}
                  isDisabled={!userCanUpdateSubscriptions}
                >
                  <FaCog className={css({ color: "#777", fontSize: "large" })} />
                </Button>

                {userCanDeleteSubscriptions && (
                  <ConfirmationModal
                    title={t(($) => $.amc["Confirm Unsubscription"])}
                    confirmButtonText={t(($) => $.amc["Confirm"])}
                    confirmationMessage={
                      <p className="fs-4 w-75 mx-auto pt-0 pb-4">
                        {t(
                          ($) =>
                            $.amc[
                              "Are you sure you want to unsubscribe from the {displayName} query? If you continue, reports will no longer be generated for this query effective immediately."
                            ],
                          { displayName: display_name },
                        )}
                      </p>
                    }
                    onConfirm={() => {
                      unsubscribeHandler.mutate(id);
                    }}
                    inlineModal={true}
                    inlineButtonContainerStyles={{}}
                    modalButtonText={
                      <button
                        className={buttonRecipe({ variant: "contentOnly" })}
                        disabled={!userCanDeleteSubscriptions}
                      >
                        <FaTrash className={css({ color: "#777", fontSize: "large" })} />
                      </button>
                    }
                  />
                )}
                <div style={{ transform: "scale(.75)" }} className="pt-2">
                  <Switch
                    isSelected={subscription.enabled}
                    onChange={(isSelected) => {
                      toggleStatusHandler.mutate({
                        subscriptionId: id,
                        enabled: isSelected,
                        queryName: display_name,
                      });
                    }}
                    isDisabled={!userCanUpdateSubscriptions}
                  />
                </div>
              </Flex>
            );
          },
        },
      },
    ];
  }, [
    setSelectedQuery,
    setShowQueryForm,
    toggleStatusHandler,
    unsubscribeHandler,
    userCanDeleteSubscriptions,
    userCanUpdateSubscriptions,
    t,
  ]);

  return (
    <>
      <h3 className="pb-3 fs-2 my-0" style={{ fontWeight: 600 }}>
        {t(($) => $.amc["Subscriptions"])}
      </h3>
      <Table
        marketPlace={marketPlace}
        tableRef={tableRef}
        tableId={TABLE_ID}
        excludedFilterColumns={["name", "link", "parameters", "frequency", "actions", "subscription_title"]}
        filterWidget={true}
        {...filterProps}
        data={tableData}
        getSearchCriteria={(row) => {
          const { display_name } = row ?? {};

          return `${display_name}`;
        }}
        hideBottomPagination={true}
        hideFilterToggleIcon={true}
        additionalFilterOptions={additionalFilters}
        columnSpecs={tableColumns}
        tableRowDescription={t(($) => $.amc["Subscriptions"])}
        defaultSorted={[{ id: "id", desc: true }]}
        isLoading={isLoading}
        handleBulkChanges={() => {}}
        minRows={0}
        defaultPageSize={5}
        isSelectable={false}
        selectionKey="id"
        isExpandable={true}
        ExpandedContent={({ row }) => {
          return (
            <ExpanderContainer>
              <AMCReportsListing fullPage={false} overrideId={row.original.id} />
            </ExpanderContainer>
          );
        }}
        titleCol={{
          id: "name",
          isStatic: true,
          Header: t(($) => $.amc["Report Type"]),
          accessor: "text",
          format: "text",
          style: { width: "300px" },
          width: 200,
          Cell: (row) => {
            const { display_name: displayName } = row.original ?? {};
            return (
              <span
                style={{
                  fontWeight: 500,
                }}
              >
                {displayName}
              </span>
            );
          },
          Filter: () => null,
        }}
      />
    </>
  );
};

export default AMCSubscriptionsTable;
